import type { FC, ReactNode } from 'react'

import { BadgePercent, CalendarClock, ShieldCheck } from 'lucide-react'
import { Container } from '@/components/generals/container'
import { Eyebrow } from '@/components/generals/eyebrow'
import { AnimatedHeading } from '@/components/motion/animated-heading'
import { Reveal } from '@/components/motion/reveal'
import { FinancingCalculator } from '@/components/widgets/financing-calculator'
import { financing } from '@/constants/pricing'

interface FinancingSectionProps {
    title: string
    description: string
    showSteps?: boolean
}

const highlights = [
    { icon: BadgePercent, label: "Pay 0% APR for 12 months, on approved credit" },
    { icon: CalendarClock, label: "Spread the cost over 12 to 60 months" },
    { icon: ShieldCheck, label: "See your rate with no effect on your credit score" }
]

export const FinancingSection: FC<FinancingSectionProps> = ({ title, description, showSteps = false }): ReactNode => {
    return (
        <section id='financing' className='relative overflow-hidden bg-ink-deep py-24 text-cream md:py-32'>
            <div aria-hidden='true' className='pointer-events-none absolute -left-40 top-10 size-[36rem] rounded-full bg-ember/20 blur-[140px]' />
            <div aria-hidden='true' className='bg-noise pointer-events-none absolute inset-0 opacity-[0.05] mix-blend-overlay' />

            <Container className='relative grid gap-14 lg:grid-cols-12 lg:items-center lg:gap-12'>
                <div className='lg:col-span-6'>
                    <Eyebrow tone='light'>Pay over time</Eyebrow>

                    <AnimatedHeading className='mt-5 max-w-[14ch] text-h2 font-semibold text-balance text-cream'>
                        {title}
                    </AnimatedHeading>

                    <Reveal className='mt-6 max-w-lg text-lead text-cream/65'>
                        <p>{description}</p>
                    </Reveal>

                    {showSteps ? (
                        <Reveal as='ol' selector=':scope > li' className='mt-10 space-y-4'>
                            {financing.steps.map((step, index) => (
                                <li key={step} className='flex gap-4 border-t border-cream/10 pt-4'>
                                    <span className='font-mono text-[12px] text-ember-soft'>0{index + 1}</span>
                                    <span className='text-[15.5px] leading-relaxed text-cream/80'>{step}</span>
                                </li>
                            ))}
                        </Reveal>
                    ) : (
                        <Reveal as='ul' selector=':scope > li' className='mt-10 space-y-3'>
                            {highlights.map(item => (
                                <li key={item.label} className='flex items-center gap-3 text-[15px] text-cream/80'>
                                    <span className='flex size-9 items-center justify-center rounded-full bg-cream/[0.07]'>
                                        <item.icon className='size-4 text-ember-soft' aria-hidden='true' />
                                    </span>
                                    {item.label}
                                </li>
                            ))}
                        </Reveal>
                    )}
                </div>

                <Reveal className='lg:col-span-6' y={60}>
                    <FinancingCalculator />
                </Reveal>
            </Container>
        </section>
    )
}
